import React, { useEffect, useState, useRef } from 'react';
//@ts-ignore
import * as styles from './TestimonialScroll.module.css';
import ArrowLgButton from '../Global/ArrowLgButton';
import {
    KontentStringValue,
    KontentMedia,
    CTALocation,
    KontentRadioMultiChoice,
    KontentRichText,
} from '../../../types/KontentProps';
import CtaLink from '../Global/CtaLink';
import RichText from '../Global/RichText';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import {
    getMediaData,
    getMultiChoice,
    optimizeKontentImageUrl,
    buildKontentSrcSet,
} from '../Global/DataUtils';
import { graphql } from 'gatsby';

type WindowAction = 'New Window' | 'Same Window';
type Scroll = {
    id?: number;
    elements: {
        cta_configuration__cta_label: KontentStringValue;
        cta_configuration__cta_location: CTALocation;
        cta_configuration__cta_location_anchor_point: KontentStringValue;
        cta_configuration__cta_window_action: KontentRadioMultiChoice<WindowAction>;
        image: KontentMedia;

        partner_name: KontentStringValue;

        partner_description: KontentStringValue;
        testimonial_quote: KontentRichText;
    };
};

type galleryWidth = number;
type left = number;
type width = number;
type cardWidth = number;
type showWidth = number;
type dotsToShow = number;

interface Props {
    kontentItemId?: string;
    layout: {
        value: [
            {
                name: string;
            }
        ];
    };
    id?: string;
    testimonials: {
        value: Scroll[];
    };
}

const TestimonialScroll: React.FC<Props> = ({
    testimonials,
    layout,
    kontentItemId,
}) => {
    const [currentScroll, setCurrentScroll] = useState(0);
    const [currentPosition, setCurrentPosition] = useState(0);
    const [width, setWidth] = useState(0);
    const [left, setLeft] = useState(0);
    const [measuredCardWidth, setMeasuredCardWidth] = useState(0);
    const galleryWrap: any = useRef(null);
    const cardRef = useRef<HTMLDivElement>(null);

    const cardWidth = measuredCardWidth || (width > 360 ? 300 : 278);
    const galleryWidth = testimonials.value.length * cardWidth;
    const showWidth =
        width > 0 && cardWidth > 0
            ? Math.max(1, Math.floor(width / cardWidth))
            : 1;
    const dotsToShow = Math.ceil(testimonials.value.length / showWidth);

    let clickNext = setGalleryPosition(
        galleryWidth,
        width,
        left,
        showWidth,
        cardWidth
    );
    let newGalleryWidth = setNewGalleryWidth(galleryWidth, width, cardWidth);

    const handleClickNext = () => {
        setCurrentScroll(currentScroll + 1);

        setCurrentPosition(currentPosition + 1);
        setLeft(left - cardWidth * showWidth);
    };

    const handleClickPrev = () => {
        setCurrentScroll(currentScroll - 1);

        setCurrentPosition(currentPosition - 1);
        setLeft(left + cardWidth * showWidth);
    };

    const renderPrevButton = () => (
        <ArrowLgButton
            strokeColor="#f06623"
            direction="left"
            handleClick={handleClickPrev}
            className={styles.prevArrowButton}
        />
    );

    const renderNextButton = () => (
        <ArrowLgButton
            strokeColor="#f06623"
            direction="right"
            handleClick={handleClickNext}
            className={styles.nextArrowButton}
        />
    );

    const renderDots = (dotsToShow: number, currentPosition: number) => {
        let dots = [];

        for (let i = 0; i < dotsToShow; i++) {
            dots.push(
                <span
                    key={i}
                    style={currentPosition == i ? { background: '#ccc' } : {}}
                ></span>
            );
        }

        return dots;
    };

    const renderScrollCard = (scrollCard: Scroll, cardIdx: number) => {
        const quote = RichText(
            scrollCard.elements.testimonial_quote,
            false,
            undefined,
            undefined,
            'testimonial_quote'
        );
        const url = getMediaData(scrollCard.elements.image)?.url;
        return (
            <div
                className={styles.scrollCard}
                key={cardIdx}
                ref={cardIdx === 0 ? cardRef : undefined}
            >
                {scrollCard.elements.image.value?.length > 0 && (
                    <div data-kontent-element-codename="image">
                        <img
                            className={styles.img}
                            src={optimizeKontentImageUrl(url, {
                                w: 400,
                                fm: 'webp' as const,
                                q: 95,
                            })}
                            srcSet={buildKontentSrcSet(url, 400, {
                                fm: 'webp' as const,
                                q: 95,
                            })}
                            sizes="200px"
                            alt={scrollCard.elements.partner_name.value || ''}
                            loading="lazy"
                        />
                    </div>
                )}
                <div
                    className={styles.name}
                    data-kontent-element-codename="partner_name"
                >
                    {scrollCard.elements.partner_name.value.substring(0, 25)}
                </div>
                {scrollCard.elements?.partner_description?.value && (
                    <div
                        className={styles.subtitle}
                        data-kontent-element-codename="partner_description"
                    >
                        {scrollCard.elements.partner_description.value}
                    </div>
                )}
                <div className={styles.paragraph}>{quote}</div>

                {scrollCard.elements.cta_configuration__cta_label.value && (
                    <div>
                        <div className={styles.label}>
                            <CtaLink
                                href={BuildLinkHref(
                                    scrollCard.elements
                                        .cta_configuration__cta_location
                                )}
                                target={
                                    getMultiChoice(
                                        scrollCard.elements
                                            .cta_configuration__cta_window_action
                                    ) === 'new_window'
                                        ? '_blank'
                                        : undefined
                                }
                            >
                                {
                                    scrollCard.elements
                                        .cta_configuration__cta_label.value
                                }
                            </CtaLink>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    useEffect(() => {
        const measureCard = () => {
            if (!cardRef.current) return;
            const style = window.getComputedStyle(cardRef.current);
            const marginLeft = parseFloat(style.marginLeft) || 0;
            const marginRight = parseFloat(style.marginRight) || 0;
            const fullWidth =
                cardRef.current.offsetWidth + marginLeft + marginRight;
            if (fullWidth > 0) setMeasuredCardWidth(fullWidth);
        };

        const resizeListener = () => {
            const newWidth = galleryWrap.current?.getBoundingClientRect().width;

            if (newWidth !== width) {
                setWidth(newWidth);
                setCurrentScroll(0);
                setCurrentPosition(0);
                setLeft(0);
            }
            measureCard();
        };

        resizeListener();
        window.addEventListener('resize', resizeListener);

        return () => {
            window.removeEventListener('resize', resizeListener);
        };
    }, [width]);

    return (
        <div data-kontent-item-id={kontentItemId}>
            {layout?.value[0]?.name === 'Carousel' ? (
                <div ref={galleryWrap} className={styles.wrapper}>
                    <div
                        id={styles.gallery}
                        className={styles.gallery}
                        style={newGalleryWidth}
                    >
                        {currentScroll > 0 ? renderPrevButton() : null}
                        <div
                            className={styles.gallerytestimonials}
                            style={clickNext}
                        >
                            {testimonials?.value.map((Scroll, cardIdx) => {
                                return renderScrollCard(Scroll, cardIdx);
                            })}
                        </div>
                        {galleryWidth + 50 > width &&
                        currentPosition + 1 < dotsToShow
                            ? renderNextButton()
                            : null}
                    </div>

                    {dotsToShow > 1 && (
                        <div className={styles.dots}>
                            {renderDots(dotsToShow, currentPosition)}
                        </div>
                    )}
                </div>
            ) : (
                <div ref={galleryWrap} className={styles.container}>
                    {testimonials?.value.map((Scroll, cardIdx) => {
                        return renderScrollCard(Scroll, cardIdx);
                    })}
                </div>
            )}
        </div>
    );
};

export default TestimonialScroll;

function setGalleryPosition(
    galleryWidth: galleryWidth,
    width: width,
    marginLeft: left,
    showWidth: showWidth,
    cardWidth: cardWidth
) {
    let newWidth;

    if (galleryWidth < width) {
        if (width / showWidth > cardWidth) {
            newWidth = galleryWidth + cardWidth;
        } else {
            newWidth = width;
        }
    } else {
        newWidth = galleryWidth;
    }
    const galleryPosition: React.CSSProperties = {
        width: newWidth,
        marginLeft: marginLeft,
        transition: 'margin .5s ease-in-out',
    };

    return galleryPosition;
}

function setNewGalleryWidth(
    galleryWidth: galleryWidth,
    width: width,
    cardWidth: cardWidth
) {
    let showWidth = Math.max(1, Math.floor(width / cardWidth));
    let newGalleryWidth = showWidth * cardWidth;

    if (newGalleryWidth > galleryWidth) {
        newGalleryWidth = galleryWidth;
    }

    const galleryInsideWidth: React.CSSProperties = {
        width: newGalleryWidth,
    };

    return galleryInsideWidth;
}

export const fragmentPartnerTestimonials = graphql`
    fragment PartnerTestimonials on kontent_item_component___partner_testimonials {
        elements {
            layout {
                value {
                    name
                }
            }
            testimonials {
                value {
                    ... on kontent_item_content_item___partner_testimonial_card {
                        id
                        elements {
                            cta_configuration__cta_label {
                                value
                            }
                            cta_configuration__cta_location {
                                value {
                                    ... on kontent_item_content_item___third_party_link {
                                        id
                                        elements {
                                            third_party_url {
                                                value
                                            }
                                        }
                                    }
                                }
                            }
                            cta_configuration__cta_location_anchor_point {
                                value
                            }
                            cta_configuration__cta_window_action {
                                value {
                                    name
                                }
                            }
                            image {
                                value {
                                    ... on kontent_item_media___image {
                                        id
                                        elements {
                                            file {
                                                value {
                                                    url
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            partner_description {
                                value
                            }
                            partner_name {
                                value
                            }
                            testimonial_quote {
                                value
                            }
                        }
                    }
                }
            }
        }
    }
`;
